package com.routexk.quiz.ui.utils

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.CSSLengthOrPercentageNumericValue
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.style.breakpoint.ResponsiveValues
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*

object Responsive {

    @OptIn(ExperimentalWasmJsInterop::class)
    @Composable
    fun <T> responsive(
        mobile: T,
        tablet: T? = null,
        desktop: T? = null,
        wide: T? = null
    ): T {
        return ResponsiveValues(
            base = mobile,
            sm = mobile,
            md = tablet ?: mobile,
            lg = desktop ?: tablet ?: mobile,
            xl = wide ?: desktop ?: tablet ?: mobile,
            xxl = wide ?: desktop ?: tablet ?: mobile
        ).get()
    }

    fun clamp(min: Number, preferred: Double, max: Number): String {
        return "clamp(${min}px, ${preferred}vw, ${max}px)"
    }

    fun clampVmin(min: Number, preferred: Double, max: Number): String {
        return "clamp(${min}px, ${preferred}vmin, ${max}px)"
    }

    fun autoClamp(
        valueFor1920px: Number,
        minMultiplier: Double = 0.5,
        maxMultiplier: Double = 1.2
    ): String {
        val preferred = (valueFor1920px.toDouble() / 1920.0) * 100.0
        val min = valueFor1920px.toDouble() * minMultiplier
        val max = valueFor1920px.toDouble() * maxMultiplier
        return clamp(min.toInt(), preferred, max.toInt())
    }

    fun autoClampFlexible(
        valueFor1920px: Number,
        minMultiplier: Double = 0.5,
        maxMultiplier: Double = 1.2
    ): String {

        val preferred = (valueFor1920px.toDouble() / 1080.0) * 100.0
        val min = valueFor1920px.toDouble() * minMultiplier
        val max = valueFor1920px.toDouble() * maxMultiplier
        return clampVmin(min.toInt(), preferred, max.toInt())
    }

    fun autoClampBoth(
        valueFor1920px: Number,
        minMultiplier: Double = 0.5,
        maxMultiplier: Double = 1.2
    ): String {
        val min = valueFor1920px.toDouble() * minMultiplier
        val max = valueFor1920px.toDouble() * maxMultiplier

        val preferredVw = (valueFor1920px.toDouble() / 1920.0) * 100.0

        val preferredVh = (valueFor1920px.toDouble() / 1080.0) * 100.0

        return "clamp(${min.toInt()}px, min(${preferredVw}vw, ${preferredVh}vh), ${max.toInt()}px)"
    }
}

fun Modifier.responsiveWidth(
    valueFor1920px: Number,
    minMultiplier: Double = 0.5,
    maxMultiplier: Double = 1.2
): Modifier {
    return this.styleModifier {
        property("width", Responsive.autoClampBoth(valueFor1920px, minMultiplier, maxMultiplier))
    }
}

fun Modifier.responsiveHeight(
    valueFor1920px: Number,
    minMultiplier: Double = 0.5,
    maxMultiplier: Double = 1.2
): Modifier {
    return this.styleModifier {
        property("height", Responsive.autoClampBoth(valueFor1920px, minMultiplier, maxMultiplier))
    }
}

fun Modifier.responsivePadding(
    verticalValueFor1920px: Number,
    horizontalValueFor1920px: Number,
    minMultiplier: Double = 0.5,
    maxMultiplier: Double = 1.2
): Modifier {
    return this.styleModifier {
        property("padding", "${Responsive.autoClampBoth(verticalValueFor1920px, minMultiplier, maxMultiplier)} ${Responsive.autoClampBoth(horizontalValueFor1920px, minMultiplier, maxMultiplier)}")
    }
}

fun Modifier.responsiveFontSize(
    valueFor1920px: Number,
    minMultiplier: Double = 0.5,
    maxMultiplier: Double = 1.2
): Modifier {
    return this.styleModifier {
        property("font-size", Responsive.autoClampBoth(valueFor1920px, minMultiplier, maxMultiplier))
    }
}

fun Modifier.responsiveBorderRadius(
    valueFor1920px: Number,
    minMultiplier: Double = 0.5,
    maxMultiplier: Double = 1.2
): Modifier {
    return this.styleModifier {
        property("border-radius", Responsive.autoClampBoth(valueFor1920px, minMultiplier, maxMultiplier))
    }
}

fun Modifier.responsiveGap(
    valueFor1920px: Number,
    minMultiplier: Double = 0.5,
    maxMultiplier: Double = 1.2
): Modifier {
    return this.styleModifier {
        property("gap", Responsive.autoClampBoth(valueFor1920px, minMultiplier, maxMultiplier))
    }
}

@Composable
fun isMobile(): Boolean {
    var isMobile by remember {
        mutableStateOf(
            try {
                window.innerWidth < 768
            } catch (e: Throwable) {
                false
            }
        )
    }

    DisposableEffect(Unit) {
        val updateIsMobile: (dynamic) -> Unit = { _ ->
            try {
                val newValue = window.innerWidth < 768
                if (isMobile != newValue) {
                    isMobile = newValue
                }
            } catch (e: Throwable) {
            }
        }

        window.addEventListener("resize", updateIsMobile)

        onDispose {
            window.removeEventListener("resize", updateIsMobile)
        }
    }

    return isMobile
}

@Composable
fun rememberViewportWidth(): Int {
    var width by remember {
        mutableStateOf(
            try {
                window.innerWidth
            } catch (e: Throwable) {
                390
            }
        )
    }

    DisposableEffect(Unit) {
        val updateWidth: (dynamic) -> Unit = { _ ->
            try {
                val newValue = window.innerWidth
                if (width != newValue) {
                    width = newValue
                }
            } catch (e: Throwable) {
            }
        }

        window.addEventListener("resize", updateWidth)

        onDispose {
            window.removeEventListener("resize", updateWidth)
        }
    }

    return width
}

object ResponsivePadding {
    val Mobile = 16.px
    val Tablet = 24.px
    val Desktop = 32.px
    val Wide = 48.px
}

object ResponsiveFontSize {
    val MobileH1 = 32.px
    val TabletH1 = 40.px
    val DesktopH1 = 48.px

    val MobileH2 = 24.px
    val TabletH2 = 30.px
    val DesktopH2 = 36.px

    val MobileBody = 14.px
    val TabletBody = 16.px
    val DesktopBody = 16.px
}
